import { AxiosError, AxiosInstance } from 'axios'
import { action, makeObservable, observable } from 'mobx'
import { getClient } from 'api'
import { authPin, requestPin } from 'components/AbTestComponents/CAT3834/utils'
import { LocalStorage } from 'utils/storage/localStorage'
import { getStorage } from 'utils/storage'
import { logWarning } from 'utils/log'
import RootStore from './RootStore'
import { formFilledJson } from 'devTools/constants'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { authDob } from 'components/AbTestComponents/CAT4197/utils'

export enum PrefillErrorKind {
  TECHNICAL = 'technical',
  MAX_ATTEMPTS = 'maxAttempts'
}

export type PIN_TYPE = 'sms' | 'email'

class PrefillStore {
  private rootStore: RootStore
  private apolloClient: AxiosInstance
  public isPrefillAvailable: boolean
  public pin: string = ''
  public dob: string = ''
  public isPinInvalid: boolean = false
  public isFinalForced: boolean = false
  public isError: boolean = false
  public errorKind: PrefillErrorKind = PrefillErrorKind.TECHNICAL
  public isPrefillSkipped: boolean = false
  public isPinVerified: boolean = false
  public isPhoneMobile: boolean = true
  public pinType: PIN_TYPE = 'sms'
  public isPrefillNotPossible: boolean = false
  public isDobVerified: boolean = false
  public isDobInvalid: boolean = false

  constructor(rootStore: RootStore) {
    this.apolloClient = getClient('apollo')
    this.isPrefillAvailable = false
    makeObservable(this, {
      getIsPrefillAvailable: action,
      isPrefillAvailable: observable,
      pin: observable,
      dob: observable,
      isPinInvalid: observable,
      setPin: action,
      setDob: action,
      setIsPinInvalid: action,
      isFinalForced: observable,
      setIsFinalForced: action,
      isError: observable,
      errorKind: observable,
      setIsError: action,
      setErrorKind: action,
      isPrefillSkipped: observable,
      setIsPrefillSkipped: action,
      isPinVerified: observable,
      setIsPinVerified: action,
      isPhoneMobile: observable,
      pinType: observable,
      isPrefillNotPossible: observable,
      isDobVerified: observable,
      isDobInvalid: observable
    })
    this.rootStore = rootStore
  }

  setIsPinVerified = (isPinVerified: boolean) => {
    this.isPinVerified = isPinVerified
  }

  setIsPrefillSkipped = (isPrefillSkipped: boolean) => {
    this.isPrefillSkipped = isPrefillSkipped
  }

  setIsFinalForced = (isFinalForced: boolean) => {
    this.isFinalForced = isFinalForced
  }

  setIsError = (value: boolean) => {
    this.isError = value
  }

  setErrorKind = (kind: PrefillErrorKind) => {
    this.errorKind = kind
  }

  setPin = (value: string) => {
    this.pin = value
  }

  setDob = (value: string) => {
    this.dob = value
  }

  setIsPinInvalid = (isPinInvalid: boolean) => {
    this.isPinInvalid = isPinInvalid
  }

  setIsPrefillAvailable = (isAvailable: boolean) => {
    this.isPrefillAvailable = isAvailable
  }

  setPinType = (pinType: PIN_TYPE) => {
    this.pinType = pinType
  }

  setIsPrefillNotPossible = (isNotPossible: boolean) => {
    this.isPrefillNotPossible = isNotPossible
  }

  getIsPrefillAvailable = async (
    advertisementId: string | null,
    traversalId: string | null,
    isVirtual?: boolean
  ) => {
    trackInAmplitude(AmplitudeEvent.PrefillStore_GetIsPrefillAvailable)
    try {
      if (!isVirtual) {
        const devConfigRaw = getStorage('localStorage').getItem(
          LocalStorage.DEV_CONFIG
        )
        if (devConfigRaw) {
          const devConfig = JSON.parse(devConfigRaw)
          if (devConfig.isForcePrefillEnabled) {
            logWarning('FORCE PREFILL FLOW / NO PRESCREEN REQUEST!')
            this.isPrefillAvailable = true
            return
          }
        }
      }

      const {
        data: { result }
      } = await this.apolloClient.get(
        `/prefill/prescreen/${advertisementId}/${traversalId}`,
        isVirtual ? { params: { isVirtual } } : undefined
      )

      if (!isVirtual) {
        this.isPrefillAvailable = true
      }
    } catch (error) {
      // e.g. {"status":400,"result":null,"errors":[{"timeout":true}]}
      this.isPrefillAvailable = false
      const axiosError = error as AxiosError

      const errorWithTimeout = new Error('Prefill prescreen failed') as any
      errorWithTimeout.status = axiosError.status

      const responseData = axiosError.response?.data as any
      const errors = Array.isArray(responseData?.errors)
        ? responseData.errors
        : []
      const timeoutError = errors.find((err: any) => err?.timeout !== undefined)
      const rejectionReasonError = errors.find(
        (err: any) => typeof err?.rejection_reason === 'string'
      )

      errorWithTimeout.timeout = timeoutError ? timeoutError.timeout : undefined
      errorWithTimeout.rejection_reason =
        responseData?.rejection_reason ||
        (rejectionReasonError
          ? rejectionReasonError.rejection_reason
          : undefined)

      throw errorWithTimeout
    }
  }

  getPrefillPin = async (
    advertisementId: string | null,
    traversalId: string | null,
    channel: string
  ) => {
    try {
      const devConfigRaw = getStorage('localStorage').getItem(
        LocalStorage.DEV_CONFIG
      )
      if (devConfigRaw) {
        const devConfig = JSON.parse(devConfigRaw)
        const simulatedStatus: number | null =
          devConfig.selectedSendPinResponseStatus || null
        if (simulatedStatus) {
          if (simulatedStatus === 200) {
            logWarning('FAKE /prefill/pin/send SUCCESS (200) SIMULATED')
            requestPin(channel as PIN_TYPE)
            return
          }
          const fakeAxiosError = { status: simulatedStatus } as AxiosError
          logWarning(`FAKE /prefill/pin/send ERROR THROWN: ${simulatedStatus}`)
          throw fakeAxiosError
        }
      }

      const response = await this.apolloClient.get(
        `/prefill/pin/send/${advertisementId}/${traversalId}?channel=${channel}`
      )
      this.setPinType(channel as PIN_TYPE)
      requestPin(channel as PIN_TYPE)
      return response
    } catch (error) {
      throw error
    }
  }

  postPrefillPinAuth = async (
    advertisementId: string | null,
    traversalId: string | null,
    pin: string | null
  ) => {
    try {
      const devConfigRaw = getStorage('localStorage').getItem(
        LocalStorage.DEV_CONFIG
      )
      if (devConfigRaw) {
        const devConfig = JSON.parse(devConfigRaw)
        const simulatedStatus: number | null =
          devConfig.selectedAuthPinResponseStatus || null
        if (simulatedStatus) {
          if (simulatedStatus === 200) {
            logWarning('FAKE /prefill/pin/auth SUCCESS (200) SIMULATED')
            authPin()

            // Build a fake data payload, merging the existing devTools formFilledJson
            // while overriding/adding the birthplace field for visual/debug purposes.
            const data = {
              result: {
                ...formFilledJson.data,
                debtors: {
                  ...formFilledJson.data.debtors,
                  primary: {
                    ...formFilledJson.data.debtors?.primary,
                    personal: {
                      ...formFilledJson.data.debtors?.primary?.personal,
                      birthplace: ''
                    }
                  }
                }
              }
            }

            this.rootStore.traversal.updateDataInTraversal(data.result)
            this.isPinVerified = true
            return
          }

          const fakeAxiosError = { status: simulatedStatus } as AxiosError
          logWarning(`FAKE /prefill/pin/auth ERROR THROWN: ${simulatedStatus}`)
          throw fakeAxiosError
        }
      }

      await this.apolloClient.post(
        `/prefill/pin/auth/${advertisementId}/${traversalId}`,
        { pin }
      )
      authPin()

      this.isPinVerified = true
      this.isPinInvalid = false
      this.setIsError(false)
    } catch (error) {
      const axiosError = error as AxiosError

      // See more details in https://smava.atlassian.net/browse/CAT-3955
      const status = axiosError.status
      switch (status) {
        case 400:
          authPin()
          this.isPinInvalid = true
          break
        case 401:
        case 408:
          authPin()
          this.isPinInvalid = true
          break
        case 404:
        case 500:
          this.isError = true
          this.errorKind = PrefillErrorKind.TECHNICAL
          break
        case 429:
          authPin()
          this.isError = true
          this.errorKind = PrefillErrorKind.MAX_ATTEMPTS
          break
        case 410:
          authPin()
          this.isPinInvalid = true
          break
        default:
          this.isPinInvalid = true
          break
      }

      throw new Error(
        'Pin authentication failed with status ' + axiosError.status
      )
    }
  }

  postPrefillDobAuth = async (
    advertisementId: string | null,
    traversalId: string | null,
    dob: string | null
  ) => {
    try {
      const {
        data: {
          result: { traversalData }
        }
      } = await this.apolloClient.post(
        `/prefill/dob/auth/${advertisementId}/${traversalId}`,
        { dob }
      )
      authDob()

      this.isDobVerified = true
      this.isDobInvalid = false
      this.rootStore.traversal.updateDataInTraversal(traversalData)
      this.setIsError(false)
    } catch (error) {
      const axiosError = error as AxiosError

      // See more details in https://smava.atlassian.net/browse/CAT-3955
      const status = axiosError.status
      switch (status) {
        case 400:
          authPin()
          this.isDobInvalid = true
          break
        case 401:
        case 408:
          authDob()
          this.isDobInvalid = true
          break
        case 404:
        case 500:
          this.isError = true
          this.errorKind = PrefillErrorKind.TECHNICAL
          break
        case 429:
          authDob()
          this.isError = true
          this.errorKind = PrefillErrorKind.MAX_ATTEMPTS
          break
        default:
          this.isDobInvalid = true
          break
      }

      throw new Error(
        'dob authentication failed with status ' + axiosError.status
      )
    }
  }
}

export default PrefillStore
